/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdStoryAction — three-button action bar (Back / Next / Cancel) that
 * sits below the story-form tab group. Dispatches a single typed
 * `storyActionEvent` whose `detail.action` discriminates the user's
 * intent (move tabs, cancel, save, or "unlock" when the vault is
 * locked and the user hit Save).
 *
 * Listens for `vault-locked` / `vault-unlocked` on `document` so the
 * Save button can flip its label between "Save" and "Unlock Vault to
 * save" without polling. (Service-level events; see vault.ts.)
 *
 * Lit light-DOM component (#206). The `stage` attribute is owned by a
 * custom getter/setter pair (not `@property`) because the setter does
 * synchronous validation — invalid stage values are silently dropped.
 * `_isVaultReady` is `@state` so vault events drive re-render through
 * Lit's normal update cycle.
 */

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { vaultService } from '../services/vault.js';
import { t, LocaleController, type MessageKey } from '../services/i18n.js';

/** Tab/stage names — also the values of the observed `stage` attribute. */
export type Stage = 'who' | 'what' | 'when';

/**
 * Every value the component dispatches on `storyActionEvent.detail.action`.
 * Includes the three `Stage` names (tab moves), plus `cancel`, `save`,
 * and `unlock` (the "vault is locked" affordance when Save is pressed
 * on the `when` stage).
 */
export type StoryAction = Stage | 'cancel' | 'save' | 'unlock';

/** Detail payload of `storyActionEvent` — single source of truth for both ends. */
export interface StoryActionDetail {
  stage: Stage;
  action: StoryAction;
}

interface StageConfig {
  /** Message key for the next-button label (#89 — resolved in render). */
  nextLabelKey: MessageKey;
  nextIcon: string;
  currentEvent: Stage;
  nextEvent: StoryAction;
  /** Back-button target stage, or `null` to suppress the back button. */
  showBack: Stage | null;
}

/** Display key per stage token — labels resolve through `t()` (#89). */
const STAGE_LABEL_KEYS: Readonly<Record<Stage, MessageKey>> = {
  who: 'storyaction.stage.who',
  what: 'storyaction.stage.what',
  when: 'storyaction.stage.when',
};

const STAGE_CONFIGS: Readonly<Record<Stage, StageConfig>> = {
  who: {
    nextLabelKey: 'storyaction.stage.what',
    nextIcon: 'arrow-right',
    currentEvent: 'who',
    nextEvent: 'what',
    showBack: null,
  },
  what: {
    nextLabelKey: 'storyaction.stage.when',
    nextIcon: 'arrow-right',
    currentEvent: 'what',
    nextEvent: 'when',
    showBack: 'who',
  },
  when: {
    nextLabelKey: 'storyaction.save',
    nextIcon: 'save',
    currentEvent: 'when',
    nextEvent: 'save',
    showBack: 'what',
  },
};

const isStage = (value: string): value is Stage =>
  value === 'who' || value === 'what' || value === 'when';

@customElement('td-story-action')
export class TdStoryAction extends LitElement {
  @state() private _stage: Stage = 'who';
  @state() _isVaultReady = false;

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  override createRenderRoot(): HTMLElement {
    return this;
  }

  static override get observedAttributes(): string[] {
    return ['stage'];
  }

  get stage(): Stage {
    return this._stage;
  }

  set stage(value: string) {
    if (isStage(value)) {
      this._stage = value;
      this.setAttribute('stage', value);
    }
  }

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    const attr = this.getAttribute('stage');
    this._stage = attr && isStage(attr) ? attr : 'who';
    document.addEventListener('vault-locked', this._onVaultLocked);
    document.addEventListener('vault-unlocked', this._onVaultUnlocked);
    this._isVaultReady = await vaultService.isVaultReady();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('vault-locked', this._onVaultLocked);
    document.removeEventListener('vault-unlocked', this._onVaultUnlocked);
  }

  override attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    // Intentionally NOT calling `super.attributeChangedCallback` — the
    // `stage` attribute is owned by the custom getter/setter above, not
    // by `@property`. Routing through super would try to look up a
    // non-existent property descriptor.
    if (oldValue === newValue || newValue === null || newValue === undefined) {
      return;
    }
    if (name === 'stage' && isStage(newValue)) {
      this._stage = newValue;
    }
  }

  private _onVaultLocked = (): void => {
    this._isVaultReady = false;
  };

  private _onVaultUnlocked = (): void => {
    this._isVaultReady = true;
  };

  getStageConfig(): StageConfig {
    return STAGE_CONFIGS[this._stage];
  }

  private _handleNext = (event: Event): void => {
    event.preventDefault();
    const config = this.getStageConfig();
    const curEvent: StoryAction =
      config.nextEvent === 'save' && !this._isVaultReady
        ? 'unlock'
        : config.nextEvent;
    // `curEvent` is one of {who, what, when, save, unlock}. Only the
    // three Stage names are valid attribute values; the others are
    // dispatch-only actions, so only narrow the assignment.
    if (isStage(curEvent)) {
      this.stage = curEvent;
    }
    this.dispatchEvent(
      new CustomEvent<StoryActionDetail>('storyActionEvent', {
        bubbles: true,
        composed: true,
        detail: { stage: this._stage, action: curEvent },
      })
    );
  };

  private _handleCancel = (event: Event): void => {
    event.preventDefault();
    this.dispatchEvent(
      new CustomEvent<StoryActionDetail>('storyActionEvent', {
        bubbles: true,
        composed: true,
        detail: { stage: this._stage, action: 'cancel' },
      })
    );
  };

  private _handleBack = (event: Event): void => {
    event.preventDefault();
    const config = this.getStageConfig();
    if (config.showBack === null) return;
    const back = config.showBack;
    this.stage = back;
    this.dispatchEvent(
      new CustomEvent<StoryActionDetail>('storyActionEvent', {
        bubbles: true,
        composed: true,
        detail: { stage: this._stage, action: back },
      })
    );
  };

  override render(): TemplateResult {
    const config = this.getStageConfig();
    const nextLabel =
      !this._isVaultReady && config.nextEvent === 'save'
        ? t('storyaction.unlockToSave')
        : t(config.nextLabelKey);

    return html`
      <div class="story-action-buttons">
        ${
          config.showBack === null
            ? nothing
            : html`
                <wa-button
                  type="button"
                  appearance="outlined"
                  pill
                  variant="neutral"
                  size="l"
                  id="backButton"
                  @click=${this._handleBack}
                >
                  <wa-icon name="arrow-left" size="l"></wa-icon>
                  <span class="back-label">
                    ${t(STAGE_LABEL_KEYS[config.showBack])}
                  </span>
                </wa-button>
              `
        }
        <wa-button
          type="button"
          appearance="outlined"
          pill
          variant="brand"
          size="l"
          id="nextButton"
          @click=${this._handleNext}
        >
          <wa-icon class="next-icon" size="l" name=${config.nextIcon}></wa-icon>
          <span class="next-label">${nextLabel}</span>
        </wa-button>
        <wa-button
          type="button"
          appearance="outlined"
          pill
          variant="danger"
          size="l"
          id="cancelButton"
          @click=${this._handleCancel}
        >
          <wa-icon name="cancel" size="m"></wa-icon>
          ${t('common.cancel')}
        </wa-button>
      </div>
    `;
  }
}
